import { Fragment, useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock, Lock } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { logError } from "@/lib/log";
import { useAuth } from "@/context/AuthContext";
import { PublicLayout } from "@/components/PublicLayout";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Questions } from "@/components/wellbeing/Questions";
import { Result } from "@/components/wellbeing/Result";
import { Trend } from "@/components/wellbeing/Trend";
import { QUESTIONS } from "@/components/wellbeing/who5";
import { track } from "@/lib/toolTrack";

const Intro = ({ onStart }) => (
  <div className="mm-card p-6 sm:p-8" data-testid="wellbeing-intro">
    <p className="text-mm-muted">Five quick questions about the last two weeks, based on the WHO-5 Wellbeing Index, a short questionnaire used by doctors and researchers around the world.</p>
    <div className="flex flex-wrap gap-4 text-sm text-mm-muted mt-4">
      <span className="inline-flex items-center gap-1.5"><Clock size={14} />About 2 minutes</span>
      <span className="inline-flex items-center gap-1.5"><Lock size={14} />No sign-up needed</span>
    </div>
    <button className="mm-btn mt-6" onClick={onStart} data-testid="wellbeing-start">Start the check</button>
  </div>
);

export default function WellbeingCheck({ inApp = false }) {
  usePageMeta({ title: "How am I really doing? Free 2-minute wellbeing check | Mindful Me",
    description: "Take a free, private 5-question wellbeing check based on the WHO-5 Wellbeing Index. Get your result in 2 minutes, with gentle next steps.", path: "/wellbeing-check" });
  const { user } = useAuth();
  const Layout = inApp ? Fragment : PublicLayout;
  const [answers, setAnswers] = useState(null);
  const [result, setResult] = useState(null);
  const [checks, setChecks] = useState([]);

  useEffect(() => {
    if (inApp) return;
    track("wellbeing-check", "visit");
    if (new URLSearchParams(window.location.search).get("shared") === "1") track("wellbeing-check", "shared_visit");
  }, [inApp]);
  const loadHistory = useCallback(() => { if (user) api.get("/wellbeing-check/history").then((r) => setChecks(r.data.checks)).catch((e) => logError("Wellbeing history failed:", e)); }, [user]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const answer = async (v) => {
    const next = [...answers, v];
    if (next.length < QUESTIONS.length) return setAnswers(next);
    try { const { data } = await api.post("/wellbeing-check", { answers: next }); if (!inApp) track("wellbeing-check", "finish"); setResult(data); setAnswers(null); loadHistory(); window.scrollTo({ top: 0, behavior: "smooth" }); }
    catch (e) { toast.error(errMsg(e, "Couldn't work out your result. Please try again.")); }
  };

  return (
    <Layout>
      <section className={`max-w-2xl space-y-6 ${inApp ? "" : "mx-auto px-5 pt-14"}`} data-testid="wellbeing-page">
        <div className="mm-stagger">
          <span className="mm-chip !cursor-default !text-xs">Free · Private · 2 minutes</span>
          <h1 className="text-4xl sm:text-5xl font-extrabold mt-4 leading-tight">How am I <span className="text-mm-primary">really</span> doing?</h1>
        </div>
        {result ? <Result r={result} signedIn={!!user} onRetake={() => { setResult(null); setAnswers([]); }} />
          : answers ? <Questions answers={answers} onAnswer={answer} onBack={() => setAnswers(answers.slice(0, -1))} />
            : <Intro onStart={() => setAnswers([])} />}
        {user && <Trend checks={checks} />}
        <p className="text-xs text-mm-muted">This check is not a diagnosis. Questions from the WHO-5 Wellbeing Index (Psychiatric Research Unit, Mental Health Centre North Zealand). {user ? "Your results are saved privately to your account." : "Nothing is saved unless you're signed in."}</p>
      </section>
    </Layout>
  );
}
